import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const BrandingContext = createContext(null);

// Default branding values
const DEFAULT_BRANDING = {
  company_name: 'FURD Tech',
  primary_color: '#1e3a5f',
  secondary_color: '#64748b',
  accent_color: '#10b981',
  logo_data: null,
  logo_version: 0,
  font_family: 'Manrope, Inter, sans-serif',
  email_signature: '',
  quote_header: '',
  quote_footer: '',
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    return {
      branding: DEFAULT_BRANDING,
      loading: false,
      refreshBranding: () => {},
      isDirector: false,
    };
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const { user, authFetch } = useAuth();
  const [branding, setBranding] = useState(() => {
    // Load from localStorage as fallback only
    const cached = localStorage.getItem('opsflow_branding');
    if (cached) {
      try {
        return { ...DEFAULT_BRANDING, ...JSON.parse(cached) };
      } catch (e) {
        return DEFAULT_BRANDING;
      }
    }
    return DEFAULT_BRANDING;
  });
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(0);

  const isDirector = user?.role === 'director';

  // Fetch branding from server
  const fetchBranding = useCallback(async (force = false) => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Debounce - don't fetch more than once per 5 seconds unless forced
    const now = Date.now();
    if (!force && now - lastFetch < 5000) {
      return;
    }

    try {
      const res = await authFetch('/team/branding');
      if (res.ok) {
        const data = await res.json();
        const newBranding = { ...DEFAULT_BRANDING, ...data.branding };
        
        setBranding(newBranding);
        setLastFetch(now);
        
        // Cache locally as fallback
        localStorage.setItem('opsflow_branding', JSON.stringify(newBranding));
        
        // Apply CSS variables
        applyBrandingCSS(newBranding);
        
        console.log('[Branding] Updated from server, version:', newBranding.logo_version);
      }
    } catch (err) {
      console.error('[Branding] Fetch error:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authFetch, lastFetch]);

  // Apply branding as CSS variables
  const applyBrandingCSS = useCallback((brandingData) => {
    const root = document.documentElement;
    
    root.style.setProperty('--brand-primary', brandingData.primary_color || DEFAULT_BRANDING.primary_color);
    root.style.setProperty('--brand-secondary', brandingData.secondary_color || DEFAULT_BRANDING.secondary_color);
    root.style.setProperty('--brand-accent', brandingData.accent_color || DEFAULT_BRANDING.accent_color);
    root.style.setProperty('--brand-font', brandingData.font_family || DEFAULT_BRANDING.font_family);
    
    // Create darker/lighter variants
    const primary = brandingData.primary_color || DEFAULT_BRANDING.primary_color;
    root.style.setProperty('--brand-primary-dark', adjustColor(primary, -20));
    root.style.setProperty('--brand-primary-light', adjustColor(primary, 40));
  }, []);

  // Helper to adjust color brightness
  const adjustColor = (hex, amount) => {
    let color = hex.replace('#', '');
    if (color.length === 3) {
      color = color[0] + color[0] + color[1] + color[1] + color[2] + color[2];
    }
    const num = parseInt(color, 16);
    let r = Math.min(255, Math.max(0, (num >> 16) + amount));
    let g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    let b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return '#' + (0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  // Initial fetch on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchBranding(true);
    } else {
      setBranding(DEFAULT_BRANDING);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.team_id]);

  // Apply CSS on branding change
  useEffect(() => {
    applyBrandingCSS(branding);
  }, [branding, applyBrandingCSS]);

  // Listen for branding updates via WebSocket
  useEffect(() => {
    const handleBrandingUpdate = (event) => {
      if (event.detail?.type === 'branding_update') {
        console.log('[Branding] Real-time update received');
        fetchBranding(true);
      }
    };

    window.addEventListener('ws_branding_update', handleBrandingUpdate);
    return () => window.removeEventListener('ws_branding_update', handleBrandingUpdate);
  }, [fetchBranding]);

  // Polling fallback - check every 60 seconds
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      fetchBranding(false);
    }, 60000);

    // Also refresh on app foreground
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchBranding(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, fetchBranding]);

  // Get logo URL with cache busting
  const getLogoUrl = useCallback(() => {
    if (branding.logo_data) {
      // If it's a data URL, return as-is
      if (branding.logo_data.startsWith('data:')) {
        return branding.logo_data;
      }
      // Add cache busting query string
      return `${branding.logo_data}?v=${branding.logo_version || Date.now()}`;
    }
    return null;
  }, [branding.logo_data, branding.logo_version]);

  const value = {
    branding,
    loading,
    refreshBranding: () => fetchBranding(true),
    isDirector,
    getLogoUrl,
    DEFAULT_BRANDING,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
